import { useState, useMemo, useCallback } from 'react';
import { useRouter } from 'next/router';

import SearchBox from '@com/_atoms/SearchInput';
import NextImage from '@com/_core/NextImage';
import { colors } from '@configs/Theme';
import Icon from '@utilities/icon';
import { debounce } from '@utilities/debounce';
import { useGetCarousels } from '@api/promotion/promotion.rq';
import LoadingSpinner from '@com/_atoms/LoadingSpinner';
import { MainLayout } from '@com/Layout';

const DiseasesListContainer = () => {
  const { push, query, pathname, back: goBack } = useRouter();

  const { data: carouselsData, isLoading: carouselIsLoading } =
    useGetCarousels();

  const diseasesData = useMemo(
    () =>
      carouselsData?.queryResult?.filter(
        (item) => item?.title === 'بیماری های رایج',
      ),
    [carouselsData],
  );

  const initialSearch = Array.isArray(query?.search)
    ? query.search[0]
    : query?.search || '';

  const [searchText, setSearchText] = useState<string>(initialSearch);

  const filteredDiseases = useMemo(() => {
    if (!searchText) return diseasesData;
    return diseasesData.filter((item) =>
      item.subTitle.toLowerCase().includes(searchText.toLowerCase()),
    );
  }, [searchText, diseasesData]);

  const debouncedUpdateUrl = useCallback(
    debounce((value: string) => {
      const newQuery = { ...query };
      if (value) {
        newQuery.search = value;
      } else {
        delete newQuery.search;
      }
      push({ pathname, query: newQuery }, undefined, { shallow: true });
    }, 300),
    [query, pathname, push],
  );

  const handleChangeSearchInput = (value: string) => {
    setSearchText(value);
    debouncedUpdateUrl(value);
  };

  return (
    <MainLayout
      hasHeader
      headerType="withoutLogo"
      hasBackButton
      hasBasketIcon
      backIconHandler={() => goBack()}
      title="بیماری ها"
    >
      <div className="w-full flex flex-col items-center gap-4 px-4 pb-2">
        <SearchBox value={searchText} onChange={handleChangeSearchInput} />
        {carouselIsLoading ? (
          <div className="w-full h-48 flex justify-center items-center">
            <LoadingSpinner />
          </div>
        ) : (
          <>
            {filteredDiseases?.map((item) => (
              <div
                key={item.recId}
                className="w-full flex items-center gap-2 rounded-xl border border-gray-200 p-4 cursor-pointer"
                onClick={() =>
                  push(
                    `/app/diseases/${item.recId}?title=${encodeURIComponent(
                      item.subTitle,
                    )}&from=diseases&pageNumber=1&pageSize=20`,
                  )
                }
              >
                <NextImage
                  src={item?.icon}
                  alt={item?.subTitle}
                  width={44}
                  height={44}
                />
                <p className="flex-1 text-sm">{item.subTitle}</p>
                <div className="flex items-center justify-center w-8 h-8 rounded-full bg-surface-background-primary">
                  <Icon
                    name="ArrowLeft"
                    width={1.2}
                    height={1.2}
                    fill={colors.black}
                  />
                </div>
              </div>
            ))}
          </>
        )}
      </div>
    </MainLayout>
  );
};

export default DiseasesListContainer;
